/* eslint-disable @typescript-eslint/no-unused-vars */
import { Grid, Snackbar, Typography, useMediaQuery } from "@mui/material";
import { ReactComponent as CrownIcon } from "assets/Crown.svg";
import BonusFlag from "components/BonusFlag";
import Button from "components/Buttons/ThemeButton";
import { useEnvContext } from "contexts";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import {
	IClubGift,
	IShopPurchaseOfferClub
} from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppSelector, useImagePath } from "hooks";
import { clubGiftsGaloreAssets } from "assets/cdn";
import useRewardsById from "hooks/useRewardsById";
import { WsDialogs } from "interfaces";
import { ClubGiftsGaloreResponse } from "interfaces/club-gifts-galore-v2";
import { FeatureType } from "interfaces/feature-tracker";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { featureTrackerAPI } from "redux/apis";
import { selectPoisBountyV3State, selectWebStoreState } from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { RootState } from "redux/store";
import { CrownIconV2 } from "utils/svgIcons";
import { getBackgroundUrl } from "../helpers";
import { FirstContributionBanner } from "./lib/FirstPurchaseBanner";
import RewardItem from "./lib/RewardItem";
import {
	CardContainer,
	CrossOutLine,
	CrossOutValue,
	CrossOutValue2,
	DataContainer,
	FinalMessage,
	Footer,
	ImageContainer,
	LoyaltyPoints,
	NumberValue,
	Reward,
	RewardIcon,
	RewardsContainer,
	StarBurst,
	SubTitle,
	ThanksMessage,
	TimedRewardDescription,
	Title,
	Value
} from "./styles";
import TimedReward from "./lib/TimedReward";
import { getDisplayRewards } from "helpers/getDisplayRewards";

interface Props {
	className?: string;
	offer: IShopPurchaseOfferClub;
	gift?: IClubGift;
	eventIsCompleted: boolean;
	isFirstPriority: boolean;
}

export const BoxCardMobile: React.FC<Props> = ({
	className,
	offer,
	gift,
	eventIsCompleted,
	isFirstPriority
}) => {
	const { openDialog } = useDialogModalRedux();
	const { getImage } = useImagePath("cdn");
	const starBurst = getImage(clubGiftsGaloreAssets.v1.giftBoxes.starBurst);
	const starBurstViolet = getImage(
		clubGiftsGaloreAssets.v2.giftBoxes.starBurstViolet
	);
	const { isFeatureFlagEnabled } = useEnvContext();
	const { reportEvent } = useGoogleAnalyticsContext();
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const {
		authToken,
		handleRefresh,
		openLoginToContinueDialog,
		postUserMetadata,
		suppressOrderConfirmation,
		wsStoreBuy
	} = useWebStoreContext();
	const { canContribute: isPoisActive } = useAppSelector(
		selectPoisBountyV3State
	);
	const isMobile = useMediaQuery("(max-width: 732px)");
	const giftRewards = [...(gift?.rewards ?? [])].reverse();
	const { rewards, rewardTypes } = useAppSelector(selectWebStoreState).store;

	const timedReward = giftRewards.find((reward) => {
		const item = getDisplayRewards(reward.id, {
			rewards,
			rewardTypes
		});
		return item[0].id === gift?.timedRewardType;
	});

	const filteredRewards = giftRewards.filter(
		(reward) => reward.id !== timedReward?.id
	);

	//console.log("test_05", test);

	const { data }: { data?: ClubGiftsGaloreResponse } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.CLUB_GIFT_BONUS_V2,
			isAuthenticated: !!authToken?.value
		});

	const firstContributionRewardData = useRewardsById(
		data?.tracker?.firstContributionRewardId as string
	)?.[0];

	const isFirstContribution =
		isFeatureFlagEnabled(FeaturesFlags.CLUB_GIFT_FIRST_CONTRIBUTION_REWARD) &&
		!eventIsCompleted &&
		data?.tracker?.firstContributionRewardId &&
		!data.tracker.userEventTracker.firstHistoricContributionRewardDate &&
		!data.tracker.userClubTracker.historicContributionCount;

	const showOfferId = useSelector(
		(state: RootState) => state.debug.showDebugLabels
	);

	const validateToken = () => {
		if (!authToken?.value) {
			openLoginToContinueDialog();
			return false;
		}
		return true;
	};

	const onClickBuy = async () => {
		if (!validateToken()) {
			return;
		}

		const invoice = await wsStoreBuy({ offerId: offer.id });
		if (invoice) {
			openDialog(WsDialogs.XSOLLA_PURCHASE, {
				xsollaToken: invoice.xsollaToken,
				xsollaHost: invoice.xsollaHost,
				invoice,
				section: "club_shop",
				source: "gift_boxes",
				pricePoint: offer.price,
				priority: offer.priority,
				cbClose: () => {
					reportEvent("dialog_x_closed", { source: "xsolla_dialog" });
					handleRefresh();
				},
				suppressOrderConfirmation,
				postUserMetadata
			});
		}
	};

	const isEventBannerFlagEnabled = () => {
		return (
			data?.tracker?.userClubTracker.isUserQualifiedToContribute &&
			!eventIsCompleted &&
			offer.id === data?.tracker?.clubGiftId
		);
	};

	const handleCopyOfferId = () => {
		navigator.clipboard.writeText(offer.id);
		setSnackbarOpen(true);
	};

	return (
		<CardContainer
			className={className}
			eventIsCompleted={eventIsCompleted}
			isHighlighted={offer.id === data?.tracker?.clubGiftId}
			isUserQualifiedToContribute={
				data?.tracker?.userClubTracker.isUserQualifiedToContribute
			}
			color="#FFF"
		>
			<ImageContainer
				image={
					isEventBannerFlagEnabled()
						? getBackgroundUrl(
								data?.tracker?.qualifiedForPurchaseChestImageUrl || "",
								"club_gifts_galore_variant",
								isMobile
						  )
						: getBackgroundUrl(
								gift?.chestImageUrl || "",
								"club_box_variant",
								isMobile
						  )
				}
			>
				<Title>
					{offer.description.split("+").map((text, index) => (
						<span key={index} style={{ whiteSpace: "nowrap" }}>
							{text}
							{offer.description.split("+").length - 1 > index && (
								<>
									<span>&nbsp;+</span>
									<br />
								</>
							)}
						</span>
					))}
				</Title>
			</ImageContainer>

			<BonusFlag
				position={"right small"}
				text={isEventBannerFlagEnabled() ? "CLUB EVENT DEAL" : offer.bannerText}
			/>

			<DataContainer>
				<SubTitle>All members get:</SubTitle>
				<SubTitle>
					You also get:
					{isFirstPriority &&
						isFirstContribution &&
						firstContributionRewardData && (
							<FirstContributionBanner
								firstContributionText="1st time"
								firstContributionSubText="contribution"
								firstContributionRewardBonus={`+${new Intl.NumberFormat(
									"en-US",
									{
										notation: "compact",
										compactDisplay: "short",
										maximumFractionDigits: 1,
										roundingMode: "floor"
									}
								).format(Number(firstContributionRewardData.description))}`}
								firstContributionRewardBonusSubText={
									Number(firstContributionRewardData.description) > 1
										? firstContributionRewardData.pluralLabel
										: firstContributionRewardData.singularLabel
								}
								firstContributionRewardBonusFinalText="for YOU!"
							/>
						)}
				</SubTitle>
				<RewardsContainer className={className} borders="10px 0 0 10px">
					<Reward>
						<RewardIcon
							src={gift?.randomRewardImageUrl}
							className={
								Array.isArray(filteredRewards) && filteredRewards?.length >= 2
									? "small-items"
									: ""
							}
						/>
						<NumberValue style={{ paintOrder: "stroke fill" }}>
							{gift?.randomRewardCount}
						</NumberValue>
						{offer?.rewardCrossout && (
							<CrossOutValue className="reward">
								<CrossOutLine className="reward" />
								{gift?.randomRewardCrossout && (
									<>{gift?.randomRewardCrossout}</>
								)}
							</CrossOutValue>
						)}
						{gift?.highlightRandomReward && (
							<StarBurst src={starBurst}></StarBurst>
						)}
					</Reward>
					{filteredRewards.map((reward, index) => {
						return (
							<RewardItem
								key={reward.id}
								reward={reward}
								filteredRewardsLength={filteredRewards.length}
								offerRewardCrossout={offer?.rewardCrossout}
								isHighlightReward={
									(gift?.highlightReward && index === 1) ||
									(gift?.highlightReward2 && index === 0)
								}
							/>
						);
					})}

					{timedReward && (
						<TimedReward>
							<div style={{ width: "40%" }}>
								<RewardItem
									key={timedReward.id}
									reward={timedReward}
									filteredRewardsLength={filteredRewards.length}
									offerRewardCrossout={offer?.rewardCrossout}
									isHighlightReward={true}
								/>
							</div>
							<TimedRewardDescription>
								{gift?.timedRewardDescription}
							</TimedRewardDescription>
						</TimedReward>
					)}
				</RewardsContainer>
				<RewardsContainer
					className={className}
					borders="0 10px 10px 0"
					style={{ flexWrap: "nowrap" }}
				>
					<Grid container alignItems="center" justifyContent="center" px={1}>
						<Grid item xs={isFirstPriority && isFirstContribution ? 7 : 12}>
							<Reward>
								<div>
									<RewardIcon
										src={gift?.thankYouCoinsImageUrl}
										style={{ maxHeight: "70px" }}
									/>
									{gift?.thankYouCoinsCrossout && (
										<CrossOutValue2 className="thankyoucoins" display={"block"}>
											{gift?.thankYouCoinsCrossout?.toLocaleString("en-US")}
										</CrossOutValue2>
									)}
									<Value
										className="anchor"
										style={{ paintOrder: "stroke fill" }}
									>
										{gift?.thankYouCoins?.toLocaleString("en-US")}
									</Value>
								</div>
								<ThanksMessage
									isFirstContribution={
										!!isFirstPriority && !!isFirstContribution
									}
								>
									Free Coins for every THANKS received!
								</ThanksMessage>
							</Reward>
						</Grid>
						{isFirstPriority && isFirstContribution && (
							<Grid item xs={5}>
								<Grid container justifyContent="center">
									<Grid item width="auto">
										<Reward>
											<RewardIcon src={firstContributionRewardData?.imageUrl} />
											<NumberValue style={{ paintOrder: "stroke fill" }}>
												{firstContributionRewardData?.description}
											</NumberValue>
											{offer?.rewardCrossout && (
												<CrossOutValue className="reward">
													<CrossOutLine className="reward" />
													{offer?.rewardCrossout && (
														<>{offer?.rewardCrossout}</>
													)}
												</CrossOutValue>
											)}
											<StarBurst src={starBurstViolet} />
										</Reward>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</RewardsContainer>
			</DataContainer>
			<Footer>
				<LoyaltyPoints
					className={offer?.loyaltyPointCrossout ? "crossout" : ""}
				>
					{offer?.loyaltyPointCrossout && (
						<CrossOutValue display={"none"}>
							<CrossOutLine />
							Earn
							{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
								<CrownIconV2 />
							) : (
								<CrownIcon width={30} height={16} />
							)}
							{Number(offer?.loyaltyPointCrossout).toLocaleString("en-US")}
						</CrossOutValue>
					)}

					<div
						style={{
							display: "flex",
							gap: isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? "2px" : "unset"
						}}
					>
						<span>Earn </span>
						{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
							<CrownIconV2 />
						) : (
							<CrownIcon width={30} height={16} />
						)}
						<span>{gift?.loyaltyPointReward.toLocaleString("en-US")}</span>
					</div>
				</LoyaltyPoints>
				<Button
					variant="contained"
					color="success"
					size="large"
					sx={{
						width: "48%",
						paddingTop: "0.5rem",
						paddingBottom: "0.5rem",
						fontSize: "16px",
						height: "46px"
					}}
					onClick={onClickBuy}
				>
					{offer.price} USD
				</Button>
			</Footer>
			<FinalMessage>
				{offer.id === data?.tracker?.clubGiftId &&
					data?.tracker?.userClubTracker.isUserQualifiedToContribute && (
						<div style={{ color: "#834800", lineHeight: "18px" }}>
							...and contribute to the Club Event!
						</div>
					)}
				{/*
					{isPoisActive && (
						<div style={{ color: "#FBBD05", lineHeight: "18px" }}>
							Contribute to Poi’s Bounty
						</div>
					)}
				*/}
			</FinalMessage>

			{showOfferId && (
				<>
					<Typography
						onClick={handleCopyOfferId}
						sx={{
							fontWeight: 700,
							color: "red",
							fontSize: "10px",
							height: "auto",
							textAlign: "center",
							marginTop: "10px",
							cursor: "pointer"
						}}
					>
						{offer.id}
					</Typography>
					<Snackbar
						open={snackbarOpen}
						autoHideDuration={2000}
						onClose={() => setSnackbarOpen(false)}
						message="Offer ID copied to clipboard"
					/>
				</>
			)}
		</CardContainer>
	);
};
